/* global React, Icon, fmt, fmt0, PlummeI18n */
// ============================================================
// AI Rådgiver — morgenbrief-hero
// ============================================================
// Heroen er dagens brief, ikke en oppsummering av hele året: statuslinje, ledetekst,
// tre effektfliser og et nattarbeidspanel. Alle tall kommer fra rådgivermodellen —
// heroen regner ingenting selv.

const AII_HERO_TONE_DOT = { green: 'var(--success)', amber: 'var(--warning)', red: 'var(--error)', none: 'var(--muted)' };

// Effektflisene viser én effekttype hver og summeres aldri på tvers. `valueKind`
// avgjør formatet: beløp går gjennom fmt(), antall står som rent tall.
function AiiHeroEffects({ effects }) {
  if (!effects?.length) return null;
  return <div className="aii-hero-effects" data-help={t('aiHelp.advisor.effects.body')} data-help-title={t('aiHelp.advisor.effects.title')}>
    {effects.map((effect, i) => <span key={i} className="aii-hero-eff">
      <span className="lbl">{t(effect.noteKey === 'vatToCover' ? 'adv.hero.vatToCover' : 'adv.effect.' + effect.type)}</span>
      <span className="val">{effect.valueKind === 'amount' ? fmt(effect.value) : String(effect.value)}</span>
      <span className="note">{t('adv.hero.effectNote.' + effect.noteKey)}</span>
    </span>)}
  </div>;
}

// Nattarbeidspanelet teller det modellbyggingen faktisk gikk gjennom. Er en linje 0,
// vises den ikke — vi later ikke som om vi kontrollerte noe som ikke fantes.
function AiiOvernightPanel({ overnight }) {
  if (!overnight?.counts) return null;
  const counts = overnight.counts;
  const rows = [
    ['transactions', counts.transactions],
    ['vouchers', counts.vouchers],
    ['risks', counts.risks],
    ['recommendations', counts.recommendations],
  ].filter(([, count]) => Number(count) > 0);
  if (!rows.length) return null;
  // Klokkeslett har ingen egen formatter i PlummeI18n; locale hentes derfra så tiden
  // følger språkvalget uten å hardkode nb-NO.
  const updatedAt = overnight.updatedAt ? new Date(overnight.updatedAt) : null;
  const updated = updatedAt && !Number.isNaN(updatedAt.getTime())
    ? updatedAt.toLocaleTimeString(PlummeI18n.intlLocale?.() || undefined, { hour: '2-digit', minute: '2-digit' })
    : null;
  return <div className="aii-overnight" data-help={t('aiHelp.advisor.overnight.body')} data-help-title={t('aiHelp.advisor.overnight.title')}>
    <div className="aii-overnight-head"><span className="aii-overnight-pulse" />{t('adv.overnight.title')}</div>
    <ul className="aii-overnight-list">
      {rows.map(([key, count]) => <li key={key}>
        <Icon name="check" size={14} />{t('adv.overnight.' + key, { count: fmt0(count) })}
      </li>)}
    </ul>
    {updated && <div className="aii-overnight-foot"><Icon name="clock" size={13} />{t('adv.overnight.updated', { time: updated })}</div>}
  </div>;
}

// Ledeteksten er den genererte dagsinnsikten når den finnes, ellers modellens egen
// helsevurdering. Vi skriver aldri «god morgen, <navn>» uten å ha et navn.
const aiiHeroGreeting = (name) => {
  const hour = new Date().getHours();
  const part = hour < 10 ? 'morning' : hour < 17 ? 'day' : 'evening';
  return name ? t('adv.hero.greetingNamed.' + part, { name }) : t('adv.hero.greeting.' + part);
};

function AiiAdvisorHero({
  compact, year, generatedAt, analysisStale, loading, overnight, effects, healthTone, healthWord, healthNote, leadText, userName,
  taskCount, onOpenTasks, onOpenAnalysis, onAsk, onCreateReport, analysisAvailable, plumSrc,
}) {
  return <div className={'aii-hero' + (compact ? ' aii-hero-compact' : '')}
    data-help={t('aiHelp.advisor.brief.body')} data-help-title={t('aiHelp.advisor.brief.title')}>
    <div className="aii-hero-grid">
      <div className="aii-hero-brief">
        <div className="aii-hero-tag"><Icon name="sparkle" size={16} />{t('adv.hero.briefTag', { year })}</div>
        {loading
          ? <div className="aii-hero-lead" role="status" aria-live="polite">{t('adv.generated.loading')}</div>
          : <React.Fragment>
            {healthWord && <div className="aii-hero-status">
              <span className="aii-hero-status-dot" style={{ background: AII_HERO_TONE_DOT[healthTone] || AII_HERO_TONE_DOT.none }} />
              <span>{t('adv.hero.healthLead')} <b>{healthWord}</b>{healthNote ? ` — ${healthNote}` : ''}</span>
            </div>}
            {!compact && <div className="aii-hero-lead">{aiiHeroGreeting(userName)} {leadText}</div>}
            {!compact && <AiiHeroEffects effects={effects} />}
            <div className="aii-hero-context">
              <span>{generatedAt ? t('adv.hero.analysisUpdated', { date: PlummeI18n.dateTime(generatedAt, { dateStyle: 'medium', timeStyle: 'short' }) }) : t('adv.hero.localBasis')}{analysisStale ? ' · ' + t('adv.hero.newData') : ''}</span></div>
            <div className="aii-hero-foot">
              {taskCount > 0 && <button type="button" className="aii-hero-pill aii-hero-pill-btn" onClick={onOpenTasks}>
                <Icon name="checkCircle" size={15} />{t('adv.hero.tasksToday', { count: taskCount })}
              </button>}
              {analysisAvailable && <button type="button" className="aii-hero-pill aii-hero-pill-btn" onClick={onOpenAnalysis}
                data-help={t('aiHelp.advisor.analysis.body')} data-help-title={t('aiHelp.advisor.analysis.title')}>
                <Icon name="fileText" size={15} />{t('adv.hero.seeAnalysis')}
              </button>}
              <button type="button" className="aii-hero-pill aii-hero-pill-btn" onClick={onAsk}
                data-help={t('aiHelp.advisor.chat.body')} data-help-title={t('aiHelp.advisor.chat.title')}>
                <Icon name="sparkle" size={15} />{t('adv.hero.ask')}
              </button>
              {onCreateReport && <button type="button" className="aii-hero-pill aii-hero-pill-btn" onClick={onCreateReport} data-help={t('helpTopics.generatedReport.body')} data-help-title={t('helpTopics.generatedReport.title')}>
                <Icon name="fileText" size={15} />{t('adv.rpt.hero.cta')}
              </button>}
            </div>
          </React.Fragment>}
      </div>
      <div className="aii-hero-side">
        {!compact && !loading && <AiiOvernightPanel overnight={overnight} />}
        <button type="button" className="aii-plum-launch" onClick={onAsk} title={t('adv.hero.chatTitle')}
          data-help={t('aiHelp.advisor.chat.body')} data-help-title={t('aiHelp.advisor.chat.title')}>
          <img className="aii-hero-plum" src={plumSrc} alt="SuperPlumma" />
          <span className="aii-plum-hint"><Icon name="sparkle" size={13} />{t('adv.hero.chatHint')}</span>
        </button>
      </div>
    </div>
  </div>;
}

Object.assign(window, { AiiAdvisorHero, AiiOvernightPanel, AiiHeroEffects });
